<template>
  <v-dialog
    :model-value="modelValue"
    max-width="1280px"
    persistent
    scrollable
    data-testid="tax-category-manager-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="tax-category-dialog-card" @keydown.esc="closeDialog">
      <v-card-title class="bg-primary text-white d-flex align-center pa-4">
        <v-icon class="mr-2">mdi-tag-multiple-outline</v-icon>
        <div class="flex-grow-1">
          <div class="text-h6">Tax Category</div>
          <div class="text-caption opacity-80">
            เพิ่ม แก้ไข และจัดลำดับ category ที่ใช้กับ Tax Configuration
          </div>
        </div>
        <v-btn icon variant="text" color="white" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <v-row align="center" class="mb-3">
          <v-col cols="12" md="7">
            <div class="text-subtitle-1 font-weight-medium">
              <v-icon class="mr-1">mdi-format-list-numbered</v-icon>
              Category List
            </div>
            <p class="text-caption text-grey mt-1 mb-0">
              ลำดับนี้จะถูกใช้กับ Tax Information, Dialog และ Tax Sandbox
            </p>
          </v-col>

          <v-col cols="12" md="5" class="d-flex flex-wrap justify-end ga-2">
            <v-btn
              data-testid="add-category-btn"
              color="primary"
              class="control-btn create-btn"
              variant="flat"
              size="large"
              prepend-icon="mdi-plus-circle"
              rounded="lg"
              @click="openCategoryDialog()"
            >
              ADD CATEGORY
              <v-tooltip activator="parent" location="top">Add new tax category</v-tooltip>
            </v-btn>
          </v-col>
        </v-row>

        <v-data-table
          data-testid="tax-category-table"
          :headers="headers"
          :items="categories"
          :loading="loading"
          class="elevation-1 my-data-table"
          density="comfortable"
          :items-per-page="-1"
          fixed-header
          hide-default-footer
          hide-default-body
        >
          <template v-slot:colgroup>
            <col
              v-for="header in headers"
              :key="header.key"
              :style="{ width: header.width, minWidth: header.width }"
            />
          </template>

          <template v-slot:tbody>
            <draggable
              v-if="categories.length"
              v-model="categories"
              item-key="categoryID"
              tag="tbody"
              class="v-data-table__tbody tax-category-draggable-body"
              handle=".drag-handle"
              ghost-class="drag-ghost"
              chosen-class="drag-chosen"
              drag-class="drag-active"
              :animation="180"
              :disabled="dragDisabled"
              @start="onDragStart"
              @end="onDragEnd"
            >
              <template #item="{ element, index }">
                <tr class="v-data-table__tr tax-category-row" :class="{ 'row-reordering': reordering }">
                  <td class="v-data-table__td text-center order-column">
                    <div class="order-cell">
                      <v-btn
                        variant="text"
                        density="compact"
                        icon
                        class="drag-handle"
                        :disabled="dragDisabled"
                      >
                        <v-icon size="18">mdi-drag-vertical</v-icon>
                        <v-tooltip activator="parent" location="top">Drag to reorder</v-tooltip>
                      </v-btn>
                      <v-chip color="primary" variant="tonal" size="small">
                        {{ element.sortOrder || index + 1 }}
                      </v-chip>
                    </div>
                  </td>
                  <td class="v-data-table__td code-column">
                    <span class="table-text font-weight-medium">{{ element.categoryCode }}</span>
                  </td>
                  <td class="v-data-table__td label-column">
                    <span class="table-text">{{ element.labelTh }}</span>
                  </td>
                  <td class="v-data-table__td label-column">
                    <span class="table-text">{{ element.labelEn }}</span>
                  </td>
                  <td class="v-data-table__td label-column">
                    <span class="table-text">{{ element.labelCn }}</span>
                  </td>
                  <td class="v-data-table__td active-column">
                    <div class="d-flex justify-center">
                      <v-switch
                        :model-value="element.isActive"
                        color="success"
                        density="compact"
                        hide-details
                        inset
                        @update:model-value="toggleActive(element, $event)"
                      />
                    </div>
                  </td>
                  <td class="v-data-table__td actions-column">
                    <div class="action-group">
                      <v-btn
                        variant="text"
                        density="comfortable"
                        icon
                        :disabled="index === 0 || reordering"
                        class="action-btn"
                        @click="moveCategory(index, -1)"
                      >
                        <v-icon size="18">mdi-arrow-up</v-icon>
                        <v-tooltip activator="parent" location="top">Move Up</v-tooltip>
                      </v-btn>

                      <v-btn
                        variant="text"
                        density="comfortable"
                        icon
                        :disabled="index === categories.length - 1 || reordering"
                        class="action-btn"
                        @click="moveCategory(index, 1)"
                      >
                        <v-icon size="18">mdi-arrow-down</v-icon>
                        <v-tooltip activator="parent" location="top">Move Down</v-tooltip>
                      </v-btn>

                      <v-btn
                        variant="text"
                        density="comfortable"
                        icon
                        class="action-btn"
                        @click="openCategoryDialog(element)"
                      >
                        <v-icon size="18">mdi-pencil-outline</v-icon>
                        <v-tooltip activator="parent" location="top">Edit</v-tooltip>
                      </v-btn>

                      <v-btn
                        variant="text"
                        density="comfortable"
                        icon
                        class="action-btn delete-btn"
                        @click="openDeleteDialog(element)"
                      >
                        <v-icon size="18">mdi-delete-outline</v-icon>
                        <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                      </v-btn>
                    </div>
                  </td>
                </tr>
              </template>
            </draggable>
            <tbody v-else class="v-data-table__tbody">
              <tr>
                <td :colspan="headers.length">
                  <div class="no-data-container">
                    <div class="no-data-content">
                      <v-icon size="64" color="grey-lighten-2" class="no-data-icon">
                        mdi-tag-multiple-outline
                      </v-icon>
                      <div class="no-data-text">
                        <h3 class="no-data-title">No Categories Found</h3>
                        <p class="no-data-subtitle">Create your first tax category to get started.</p>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn data-testid="tax-category-dialog-close-btn" variant="outlined" @click="closeDialog">
          <v-icon start>mdi-close</v-icon>
          Close
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="categoryDialog" max-width="720px" persistent data-testid="category-dialog">
      <v-card>
        <v-card-title class="bg-primary text-white">
          <v-icon class="mr-2">{{ editMode ? 'mdi-pencil' : 'mdi-plus-circle' }}</v-icon>
          {{ editMode ? 'Edit Category' : 'Add Category' }}
        </v-card-title>

        <v-card-text class="pt-4">
          <v-form ref="form" v-model="formValid">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  data-testid="category-code-input"
                  v-model="formData.categoryCode"
                  label="Code *"
                  variant="outlined"
                  density="comfortable"
                  :rules="[rules.required, rules.max50]"
                  prepend-inner-icon="mdi-identifier"
                  placeholder="e.g. A"
                  @update:model-value="formData.categoryCode = String(formData.categoryCode || '').toUpperCase()"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="formData.sortOrder"
                  label="Sort Order *"
                  type="number"
                  min="1"
                  variant="outlined"
                  density="comfortable"
                  :rules="[rules.required, rules.positiveNumber]"
                  prepend-inner-icon="mdi-sort-numeric-ascending"
                />
              </v-col>
            </v-row>

            <v-text-field
              data-testid="category-labelTh-input"
              v-model="formData.labelTh"
              label="In Thai *"
              variant="outlined"
              density="comfortable"
              :rules="[rules.required, rules.max255]"
              prepend-inner-icon="mdi-tag-outline"
              placeholder="e.g. ค่าลดหย่อนพื้นฐาน/ครอบครัว"
            />

            <v-text-field
              v-model="formData.labelEn"
              label="In English *"
              variant="outlined"
              density="comfortable"
              :rules="[rules.required, rules.max255]"
              prepend-inner-icon="mdi-tag-outline"
              placeholder="e.g. Personal and family allowances"
            />

            <v-text-field
              v-model="formData.labelCn"
              label="In Chinese *"
              variant="outlined"
              density="comfortable"
              :rules="[rules.required, rules.max255]"
              prepend-inner-icon="mdi-tag-outline"
              placeholder="e.g. 个人和家庭扣除"
            />

            <v-switch
              v-model="formData.isActive"
              label="Active"
              color="success"
              inset
              hide-details
            />
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn data-testid="category-cancel-btn" variant="outlined" @click="closeCategoryDialog">
            <v-icon start>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn
            data-testid="category-save-btn"
            color="primary"
            variant="elevated"
            :disabled="!formValid"
            :loading="saving"
            @click="saveCategory"
          >
            <v-icon start>mdi-content-save</v-icon>
            {{ editMode ? 'Save' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="500px" persistent data-testid="category-delete-dialog">
      <v-card>
        <v-card-title class="bg-error text-white">
          <v-icon class="mr-2">mdi-alert-circle</v-icon>
          Confirm Delete
        </v-card-title>

        <v-card-text class="pt-4">
          <v-alert type="warning" variant="tonal" class="mb-4">
            This action cannot be undone.
          </v-alert>
          <p>Are you sure you want to delete the category:</p>
          <p class="font-weight-bold text-primary">
            {{ selectedCategory?.categoryCode }} - {{ selectedCategory?.labelTh }}
          </p>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn data-testid="category-delete-cancel-btn" variant="outlined" @click="deleteDialog = false">
            <v-icon start>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn
            data-testid="category-delete-confirm-btn"
            color="error"
            variant="elevated"
            :loading="deleting"
            @click="confirmDelete"
          >
            <v-icon start>mdi-delete</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" location="top" timeout="3000">
      {{ snackbarMessage }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-dialog>
</template>

<script>
import draggable from 'vuedraggable';

export default {
  name: 'TaxCategoryDialog',
  components: { draggable },
  props: {
    modelValue: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'changed'],
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      reordering: false,
      dragPreviousCategories: [],
      categories: [],
      headers: [
        { title: 'Order', key: 'sortOrder', align: 'center', sortable: false, width: '130px' },
        { title: 'Code', key: 'categoryCode', align: 'start', sortable: false, width: '140px' },
        { title: 'In Thai', key: 'labelTh', align: 'start', sortable: false, width: '230px' },
        { title: 'In English', key: 'labelEn', align: 'start', sortable: false, width: '230px' },
        { title: 'In Chinese', key: 'labelCn', align: 'start', sortable: false, width: '230px' },
        { title: 'Active', key: 'isActive', align: 'center', sortable: false, width: '110px' },
        { title: 'Actions', key: 'actions', align: 'center', sortable: false, width: '220px' },
      ],
      categoryDialog: false,
      editMode: false,
      formValid: false,
      formData: {
        categoryID: 0,
        categoryCode: '',
        labelTh: '',
        labelEn: '',
        labelCn: '',
        sortOrder: 1,
        isActive: true,
      },
      selectedCategory: null,
      deleteDialog: false,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      rules: {
        required: (value) => value !== null && value !== undefined && String(value).trim() !== '' || 'Required',
        max50: (value) => String(value || '').length <= 50 || 'Maximum 50 characters',
        max255: (value) => String(value || '').length <= 255 || 'Maximum 255 characters',
        positiveNumber: (value) => Number(value) > 0 || 'Sort order must be greater than 0',
      },
    };
  },
  computed: {
    dragDisabled() {
      return this.loading || this.saving || this.deleting || this.reordering || this.categories.length < 2;
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.loadCategories();
      }
    },
  },
  mounted() {
    if (this.modelValue) {
      this.loadCategories();
    }
  },
  methods: {
    getEmptyFormSeed() {
      return {
        categoryID: 0,
        categoryCode: '',
        labelTh: '',
        labelEn: '',
        labelCn: '',
        sortOrder: 1,
        isActive: true,
      };
    },

    getEmptyForm() {
      return {
        ...this.getEmptyFormSeed(),
        sortOrder: Math.max(this?.categories?.length || 0, 0) + 1,
      };
    },

    normalizeCategory(item) {
      return {
        categoryID: item.categoryID ?? item.CategoryID,
        categoryCode: item.categoryCode ?? item.CategoryCode ?? '',
        labelTh: item.labelTh ?? item.LabelTh ?? '',
        labelEn: item.labelEn ?? item.LabelEn ?? '',
        labelCn: item.labelCn ?? item.LabelCn ?? '',
        sortOrder: item.sortOrder ?? item.SortOrder ?? 0,
        isActive: item.isActive ?? item.IsActive ?? false,
        createDate: item.createDate ?? item.CreateDate,
        updateDate: item.updateDate ?? item.UpdateDate,
      };
    },

    extractErrorMessage(error, fallback) {
      return error?.response?.data?.message || error?.response?.data?.Message || error?.message || fallback;
    },

    callApi(methodName, ...args) {
      return new Promise((resolve, reject) => {
        if (!this.$call || typeof this.$call[methodName] !== 'function') {
          reject(new Error(`${methodName} is not available`));
          return;
        }

        this.$call[methodName](...args, resolve, reject);
      });
    },

    async validateForm() {
      const result = await this.$refs.form?.validate();
      return typeof result === 'object' ? result.valid : result;
    },

    async loadCategories() {
      this.loading = true;
      try {
        const response = await this.callApi('GetTaxCategories');
        if (response.status === 0) {
          this.categories = (response.data || [])
            .map(this.normalizeCategory)
            .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        } else {
          this.showError(response.message || 'Failed to load categories');
        }
      } catch (error) {
        console.error('Error loading categories:', error);
        this.showError(this.extractErrorMessage(error, 'Failed to load categories'));
      } finally {
        this.loading = false;
      }
    },

    emitChanged() {
      this.$emit('changed', this.categories.map((item) => ({ ...item })));
    },

    openCategoryDialog(item = null) {
      this.editMode = !!item;
      this.formData = item ? { ...item } : this.getEmptyForm();
      this.categoryDialog = true;
      this.$nextTick(() => this.$refs.form?.resetValidation());
    },

    closeCategoryDialog() {
      this.categoryDialog = false;
      this.editMode = false;
      this.formData = this.getEmptyForm();
    },

    closeDialog() {
      this.$emit('update:modelValue', false);
    },

    buildRequest() {
      return {
        categoryID: this.formData.categoryID || 0,
        categoryCode: String(this.formData.categoryCode || '').trim().toUpperCase(),
        labelTh: String(this.formData.labelTh || '').trim(),
        labelEn: String(this.formData.labelEn || '').trim(),
        labelCn: String(this.formData.labelCn || '').trim(),
        sortOrder: Number(this.formData.sortOrder),
        isActive: this.formData.isActive !== false,
      };
    },

    async saveCategory() {
      if (!(await this.validateForm())) return;

      this.saving = true;
      const wasEditMode = this.editMode;
      try {
        const request = this.buildRequest();
        const response = wasEditMode
          ? await this.callApi('UpdateTaxCategory', request.categoryID, request)
          : await this.callApi('CreateTaxCategory', request);

        if (response.status === 0) {
          this.closeCategoryDialog();
          await this.loadCategories();
          this.emitChanged();
          this.showSuccess(wasEditMode ? 'Category updated successfully' : 'Category created successfully');
        } else {
          this.showError(response.message || 'Failed to save category');
        }
      } catch (error) {
        console.error('Error saving category:', error);
        this.showError(this.extractErrorMessage(error, 'Failed to save category'));
      } finally {
        this.saving = false;
      }
    },

    async toggleActive(item, value) {
      const previousValue = item.isActive;
      item.isActive = value;

      try {
        const request = {
          ...item,
          isActive: value,
        };
        const response = await this.callApi('UpdateTaxCategory', item.categoryID, request);
        if (response.status === 0) {
          this.emitChanged();
          return;
        }

        item.isActive = previousValue;
        this.showError(response.message || 'Failed to update active status');
      } catch (error) {
        item.isActive = previousValue;
        console.error('Error updating active status:', error);
        this.showError(this.extractErrorMessage(error, 'Failed to update active status'));
      }
    },

    applySortOrder(items) {
      return items.map((item, idx) => ({
        ...item,
        sortOrder: idx + 1,
      }));
    },

    async saveCategoryOrder(previousItems, successMessage) {
      this.categories = this.applySortOrder(this.categories);
      this.reordering = true;

      try {
        const categoryIds = this.categories.map((item) => item.categoryID);
        const response = await this.callApi('ReorderTaxCategories', categoryIds);
        if (response.status !== 0) {
          this.categories = previousItems;
          this.showError(response.message || 'Failed to update sort order');
          return false;
        }

        this.emitChanged();
        if (successMessage) {
          this.showSuccess(successMessage);
        }

        return true;
      } catch (error) {
        this.categories = previousItems;
        console.error('Error reordering categories:', error);
        this.showError(this.extractErrorMessage(error, 'Failed to update sort order'));
        return false;
      } finally {
        this.reordering = false;
      }
    },

    onDragStart() {
      this.dragPreviousCategories = [...this.categories];
    },

    async onDragEnd(event) {
      if (!event || event.oldIndex === event.newIndex) {
        this.dragPreviousCategories = [];
        return;
      }

      await this.saveCategoryOrder(this.dragPreviousCategories, 'Category order saved');
      this.dragPreviousCategories = [];
    },

    async moveCategory(fromIndex, direction) {
      const toIndex = fromIndex + direction;
      if (toIndex < 0 || toIndex >= this.categories.length) return;

      const previousItems = [...this.categories];
      const items = [...this.categories];
      const [moved] = items.splice(fromIndex, 1);
      items.splice(toIndex, 0, moved);

      this.categories = this.applySortOrder(items);
      await this.saveCategoryOrder(previousItems, 'Category order saved');
    },

    openDeleteDialog(item) {
      this.selectedCategory = item;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      if (!this.selectedCategory) return;

      this.deleting = true;
      try {
        const response = await this.callApi('DeleteTaxCategory', this.selectedCategory.categoryID);
        if (response.status === 0) {
          this.deleteDialog = false;
          this.selectedCategory = null;
          await this.loadCategories();
          this.emitChanged();
          this.showSuccess('Category deleted successfully');
        } else {
          this.showError(response.message || 'Failed to delete category');
        }
      } catch (error) {
        console.error('Error deleting category:', error);
        this.showError(this.extractErrorMessage(error, 'Failed to delete category'));
      } finally {
        this.deleting = false;
      }
    },

    showSuccess(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'success';
      this.snackbar = true;
    },

    showError(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'error';
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.tax-category-dialog-card {
  overflow: hidden;
}

.no-data-container {
  padding: 48px 24px;
  text-align: center;
}

.no-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.no-data-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: #616161;
}

.no-data-subtitle {
  color: #9e9e9e;
  font-size: 0.875rem;
}

.action-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 190px;
}

.order-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 82px;
}

.table-text {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drag-handle {
  cursor: grab;
  color: #757575;
}

.drag-handle:active {
  cursor: grabbing;
}

.row-reordering {
  opacity: 0.72;
  pointer-events: none;
}

.drag-ghost {
  background: rgba(var(--v-theme-primary), 0.08) !important;
  opacity: 0.55;
}

.drag-chosen {
  background: rgba(var(--v-theme-primary), 0.06) !important;
}

.drag-active {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
}

.action-btn {
  transition: all 0.2s ease;
}

.action-btn:hover {
  transform: scale(1.1);
}

.delete-btn:hover {
  color: rgb(var(--v-theme-error)) !important;
}

:deep(.my-data-table .v-table__wrapper) {
  max-height: 500px !important;
  overflow: auto !important;
}

:deep(.my-data-table table) {
  table-layout: fixed;
  min-width: 1290px;
  width: 100%;
}

:deep(.my-data-table thead th),
:deep(.my-data-table tbody td) {
  vertical-align: middle;
}

:deep(.my-data-table tbody td) {
  height: 56px;
  padding: 0 16px;
}
</style>
